import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Pill } from '../../../shared/Pill';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import { QuickBooksConnectionNotice } from '../../../shared/QuickBooksItemPicker';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDateTime } from '../../../shared/tenantClock';
import FieldHelp from '../../../shared/FieldHelp';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';

// QuickBooks > Mappings (QuickBooks Setup Mappings, issue #264): each
// active Optim Tax Code and Payment Term matched once to its QuickBooks
// equivalent, and the QuickBooks Item shipping charges are invoiced
// against, so every Invoice carries the right GST, terms and freight line.
// Picked from lists read from QuickBooks; nothing is created there. A match
// stores QuickBooks' Id, so renaming on either side doesn't break it.
// Each bank Account is matched to the QuickBooks bank account its Receipts
// are copied into, and the Receipts card sets the start date they're copied
// from (issue #342).

const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD = { padding: '8px', fontSize: 14, verticalAlign: 'middle' };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };
const SELECT = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%', maxWidth: 360 };

const UNMATCHED_PILL = { color: '#b91c1c', background: '#fef2f2' };
const STATUS_PILLS = {
  inactive: { label: 'Inactive in QuickBooks', title: 'Made inactive in QuickBooks since it was matched. Reactivate it there, or match another.' },
  missing: { label: 'Not found in QuickBooks', title: 'No longer in QuickBooks as something that can be matched here. Match another.' },
};

function cardStyle(complete) {
  return { margin: '16px 24px 0', background: '#fff', border: `1px solid ${complete ? '#b3bbc7' : '#f0d58c'}`, borderRadius: 10, padding: '14px 16px' };
}

// What a match shows when not editing: its QuickBooks name, "Not matched",
// or a warning when it's gone inactive or missing in QuickBooks.
function MatchValue({ match }) {
  if (!match) return <Pill {...UNMATCHED_PILL}>Not matched</Pill>;
  const warning = STATUS_PILLS[match.status];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {match.name}
      {warning && <Pill color="#92400e" background="rgba(217, 119, 6, 0.14)" title={warning.title}>{warning.label}</Pill>}
    </span>
  );
}

// The options for a picker: QuickBooks' active records, plus the current
// match if it's no longer among them, so editing doesn't silently drop it.
function optionsWith(records, match, label) {
  const options = records.map((r) => ({ id: r.id, label: label(r) }));
  if (match && !records.some((r) => r.id === match.id)) options.unshift({ id: match.id, label: `${match.name} (${STATUS_PILLS[match.status]?.label || 'current'})`, stale: true });
  return options;
}

// One matching table: each Optim record beside its QuickBooks match.
function MatchCard({ title, helpKey, help, rows, columns, qboRecords, qboLabel, unmatchedCount, connected, canEdit, endpoint, savedNoun, onSaved }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const editing = draft !== null;

  const startEdit = () => setDraft(Object.fromEntries(rows.map((r) => [r.optim.id, r.match?.id || ''])));

  const save = async () => {
    // Only what changed is sent, so a stale match left alone isn't refused.
    const matches = Object.fromEntries(rows
      .filter((r) => (draft[r.optim.id] || '') !== (r.match?.id || ''))
      .map((r) => [r.optim.id, draft[r.optim.id] || null]));
    if (!Object.keys(matches).length) {
      setDraft(null);
      return;
    }
    setSaving(true);
    try {
      await axios.put(endpoint, { matches });
      setDraft(null);
      dismissError();
      onSaved();
    } catch (err) {
      showError(err, `Saving the ${savedNoun} matches failed`);
    }
    setSaving(false);
  };

  return (
    <div style={cardStyle(unmatchedCount === 0)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 15 }}>{title}</strong>
        <FieldHelp fieldKey={helpKey} pageKey="accounting-quickbooks" label={`QuickBooks — ${title}`}>{help}</FieldHelp>
        {unmatchedCount > 0 && <span style={{ fontSize: 13, color: '#92400e' }}>{unmatchedCount} not matched</span>}
        {!editing && canEdit && connected && rows.length > 0 && (
          <button type="button" onClick={startEdit} title={`Edits which QuickBooks record each ${savedNoun} matches`} style={{ ...LINK_BUTTON, marginLeft: 'auto' }}>
            {unmatchedCount === rows.length ? 'Match' : 'Change'}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: '#666', marginTop: 8 }}>No active {savedNoun}s.</div>
      ) : (
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, marginTop: 8 }}>
          <thead>
            <tr>
              {columns.map((c) => <th key={c.key} style={{ ...TH, width: c.width }}>{c.label}</th>)}
              <th style={TH}>QuickBooks</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.optim.id} style={{ borderBottom: '1px solid #eceef1' }}>
                {columns.map((c) => <td key={c.key} data-label={c.label} style={TD}>{c.render(row.optim)}</td>)}
                <td data-label="QuickBooks" style={TD}>
                  {editing ? (
                    <select className="styled-input" value={draft[row.optim.id]} onChange={(e) => setDraft({ ...draft, [row.optim.id]: e.target.value })} style={SELECT}>
                      <option value="">Not matched</option>
                      {optionsWith(qboRecords, row.match, qboLabel).map((o) => <option key={o.id} value={o.id} disabled={o.stale}>{o.label}</option>)}
                    </select>
                  ) : (
                    <MatchValue match={row.match} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <SaveButton type="button" onClick={save} loading={saving} />
          <CancelButton onClick={() => setDraft(null)} disabled={saving} />
        </div>
      )}
    </div>
  );
}

// The QuickBooks Item every shipping charge is invoiced against.
function ShippingCard({ match, items, connected, canEdit, onSaved }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const editing = draft !== null;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put('/api/quickbooks/mappings/shipping-item', { qbo_item_id: draft || null });
      setDraft(null);
      dismissError();
      onSaved();
    } catch (err) {
      showError(err, 'Saving the shipping Item failed');
    }
    setSaving(false);
  };

  return (
    <div style={cardStyle(!!match)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 15 }}>Shipping</strong>
        <FieldHelp fieldKey="accounting-quickbooks.shipping-item" pageKey="accounting-quickbooks" label="QuickBooks — Shipping Item">
          The QuickBooks Item every shipping charge is invoiced against: one Invoice line per shipment, using this Item. Only active Service and Non-inventory Items can be picked.
        </FieldHelp>
        {!editing && canEdit && connected && (
          <button type="button" onClick={() => setDraft(match?.id || '')} title="Edits which QuickBooks Item shipping charges are invoiced against" style={{ ...LINK_BUTTON, marginLeft: 'auto' }}>
            {match ? 'Change' : 'Choose Item'}
          </button>
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 12, color: '#5a6b85' }}>Shipping charges are invoiced against QuickBooks Service or Non-inventory Item</label>
        <div style={{ fontSize: 14, marginTop: 4 }}>
          {editing ? (
            <select className="styled-input" value={draft} onChange={(e) => setDraft(e.target.value)} style={SELECT}>
              <option value="">Not chosen</option>
              {optionsWith(items, match, (i) => i.full_name).map((o) => <option key={o.id} value={o.id} disabled={o.stale}>{o.label}</option>)}
            </select>
          ) : match ? <MatchValue match={match} /> : <Pill {...UNMATCHED_PILL}>Not chosen</Pill>}
        </div>
      </div>
      {editing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <SaveButton type="button" onClick={save} loading={saving} />
          <CancelButton onClick={() => setDraft(null)} disabled={saving} />
        </div>
      )}
    </div>
  );
}

function QuickBooksMappingsTab({ canEdit }) {
  const showError = useErrorToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async ({ refresh = false } = {}) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/quickbooks/mappings', { params: refresh ? { refresh: 1 } : {} });
      setData(res.data.data);
    } catch (err) {
      showError(err, 'Failed to load QuickBooks mappings');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const connected = data?.connection?.status === 'connected';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '16px 24px 0' }}>
        <button
          type="button"
          onClick={() => load({ refresh: true })}
          disabled={loading}
          title="Fetches the tax codes, Terms, Items and bank accounts from QuickBooks again"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', fontSize: 14, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? <Loader2 size={15} style={{ animation: 'qb-spin 0.8s linear infinite' }} /> : <RefreshCw size={15} />}
          Refresh from QuickBooks
        </button>
        {data?.fetched_at && <span style={{ fontSize: 13, color: '#666' }}>Fetched {formatDateTime(data.fetched_at)}</span>}
        <style>{'@keyframes qb-spin { to { transform: rotate(360deg); } }'}</style>
      </div>

      <QuickBooksConnectionNotice connection={data?.connection} style={{ margin: '12px 24px 0' }} />

      {data && (
        <>
          <MatchCard
            title="Tax Codes"
            helpKey="accounting-quickbooks.tax-code-matches"
            help="Which QuickBooks tax code each active Optim Tax Code becomes on an Invoice, so its GST is right. Only QuickBooks tax codes used on sales are offered. A new Tax Code shows as not matched until it's matched here."
            rows={data.tax_codes}
            columns={[
              { key: 'code', label: 'Tax Code', width: '18%', render: (t) => t.code },
              { key: 'description', label: 'Description', width: '32%', render: (t) => t.description },
              { key: 'rate', label: 'Rate', width: '12%', render: (t) => `${t.rate}%` },
            ]}
            qboRecords={data.qbo_tax_codes}
            qboLabel={(t) => (t.description && t.description !== t.name ? `${t.name} — ${t.description}` : t.name)}
            unmatchedCount={data.unmatched_tax_codes}
            connected={connected}
            canEdit={canEdit}
            endpoint="/api/quickbooks/mappings/tax-codes"
            savedNoun="Tax Code"
            onSaved={load}
          />
          <MatchCard
            title="Payment Terms"
            helpKey="accounting-quickbooks.payment-term-matches"
            help="Which QuickBooks Term each active Optim Payment Term becomes on Invoices and linked QuickBooks Customers. Changing a match updates every linked QuickBooks Customer on that Payment Term. A new Payment Term shows as not matched until it's matched here."
            rows={data.payment_terms}
            columns={[
              { key: 'name', label: 'Payment Term', width: '38%', render: (t) => t.name },
              { key: 'days', label: 'Days', width: '12%', render: (t) => t.days },
            ]}
            qboRecords={data.qbo_terms}
            qboLabel={(t) => t.name}
            unmatchedCount={data.unmatched_payment_terms}
            connected={connected}
            canEdit={canEdit}
            endpoint="/api/quickbooks/mappings/payment-terms"
            savedNoun="Payment Term"
            onSaved={load}
          />
          <ShippingCard match={data.shipping_item} items={data.qbo_shipping_items} connected={connected} canEdit={canEdit} onSaved={load} />
          <MatchCard
            title="Bank Accounts"
            helpKey="accounting-quickbooks.bank-account-matches"
            help="Which QuickBooks bank account each active Optim bank Account matches, so a Receipt paid into it lands in the same bank account in QuickBooks. A Receipt paid into an unmatched bank Account shows Not in QuickBooks until it's matched here and retried."
            rows={data.bank_accounts}
            columns={[
              { key: 'number', label: 'Account', width: '14%', render: (a) => a.account_number },
              { key: 'name', label: 'Name', width: '36%', render: (a) => a.account_name },
            ]}
            qboRecords={data.qbo_bank_accounts}
            qboLabel={(a) => (a.number ? `${a.name} (${a.number})` : a.name)}
            unmatchedCount={data.unmatched_bank_accounts}
            connected={connected}
            canEdit={canEdit}
            endpoint="/api/quickbooks/mappings/bank-accounts"
            savedNoun="bank Account"
            onSaved={load}
          />
        </>
      )}
    </>
  );
}

export default QuickBooksMappingsTab;
